import { CancelledError, useQueryClient } from '@tanstack/react-query';
import React from 'react';

import { Workspace, workspaceName } from '@src/gen/ai/h2o/workspace/v1/workspace_pb';
import { useCloudPlatformDiscovery, useUser } from '@shared-legacy/lib/hooks';
import { useSessionStorage } from '@shared/lib/hooks';
import { useToast } from '@shared/lib/ui/components-alpha';
import { SessionStorageKeys } from '@shared/lib/utils/localStorage';
import { getQueryOptionsWorkspace } from '@shared/services/authz/authz.hooks';
import { workspaceIdFromName } from '@shared/services/authz/utils/utils';

import { useWorkspaceQueries } from '../hooks/workspaceProvider';

// If you use this _path parameter_ in the app route, and the route will also use the `RequireWorkspaces` guard (via
// `requiresWorkspaces` route param), then the system will try to _activate_ the workspace specified in the URL param.
// It is like a signal that the URL contains the workspace that must be activated.
//
// @example
// Given:
//   routeParam = { path: '/workspaces/:${MAGIC_PATH_PARAM_WORKSPACE}/features', requiresWorkspaces: true }
// Then:
//  navigating on "/workspaces/123/features" will load the app with workspace "123" activated.
//
export const MAGIC_PATH_PARAM_WORKSPACE = 'workspace_id' as const;

type WorkspaceContextType = {
  ACTIVE_WORKSPACE_ID?: string;
  ACTIVE_WORKSPACE_NAME?: string;
  activateWorkspace: (name: string) => Promise<boolean>;
  activeWorkspace?: Workspace;
  activeWorkspaceInitialized: boolean;
  defaultWorkspace?: Workspace;
};

const WorkspaceContext = React.createContext<WorkspaceContextType | undefined>(undefined);

const WorkspaceProvider = ({ children }: { children: React.ReactNode }) => {
  const user = useUser();
  const { addToast } = useToast();

  const [storedWorkspace, setStoredWorkspace] = useSessionStorage<Workspace | undefined>(
    SessionStorageKeys.ACTIVE_WORKSPACE
  );

  // The active workspace used to live in localStorage, where it was shared by
  // every tab. It now lives per-tab in sessionStorage; drop the stale shared
  // value once so it can't leak back in. Intentionally NOT seeded into
  // sessionStorage: a fresh tab must start empty so it bootstraps from the URL.
  // TODO: remove in future versions once all clients have cleared localStorage
  React.useEffect(() => {
    window.localStorage.removeItem(SessionStorageKeys.ACTIVE_WORKSPACE);
  }, []);
  const activeWorkspaceId = React.useMemo<undefined | string>(
    () => workspaceIdFromName(storedWorkspace?.name),
    [storedWorkspace?.name]
  );

  const queries = useWorkspaceQueries({
    user,
    activeWorkspaceId,
  });

  const { activeWorkspace, activeWorkspaceInitialized, activeWorkspaceValid, defaultWorkspace, staleActiveWorkspace } =
    queries;

  const updatePreferences = React.useCallback(
    (workspaceToActivate: Workspace) => {
      setStoredWorkspace(workspaceToActivate);
    },
    [setStoredWorkspace]
  );

  const queryClient = useQueryClient();
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';

  const activateWorkspace = React.useCallback(
    async (workspaceToActivate: string): Promise<boolean> => {
      if (workspaceToActivate === activeWorkspace?.name) return true;
      try {
        const { workspace: workspaceId } = workspaceName.parse(workspaceToActivate);
        const workspace = await queryClient.fetchQuery({
          ...getQueryOptionsWorkspace(authzUrl || ``, workspaceId, queryClient),
          staleTime: 60 * 1_000, // 60 seconds - we can consider the workspace fresh for a short period after activation
        });
        updatePreferences(workspace);
        return true;
      } catch (error) {
        // Ignore cancellation errors (e.g., when query is cancelled due to component unmount or new query)
        if (error instanceof CancelledError) return false;
        const message = `Workspace you are trying to activate was not found.`;
        console.error(message);
        addToast({
          type: 'error',
          message,
        });
      }
      return false;
    },
    [activeWorkspace?.name, updatePreferences, queryClient, authzUrl, addToast]
  );

  React.useEffect(() => {
    if (staleActiveWorkspace) {
      setStoredWorkspace(undefined);
    }
  }, [staleActiveWorkspace, setStoredWorkspace]);

  const validatedActiveWorkspace = activeWorkspaceValid === false ? undefined : activeWorkspace;

  const value = {
    ACTIVE_WORKSPACE_ID: activeWorkspaceId,
    ACTIVE_WORKSPACE_NAME: validatedActiveWorkspace?.name,
    activateWorkspace,
    activeWorkspace: validatedActiveWorkspace,
    activeWorkspaceInitialized,
    defaultWorkspace,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

const useWorkspaces = (): WorkspaceContextType => {
  const context = React.useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspaces must be used within a WorkspaceProvider');
  }
  return context;
};

export { useWorkspaces, WorkspaceProvider };
